import { useMemo, useState } from 'react';
import { CzMaxPrice, ReferencePrice } from '~/libs/infrastructure/schemas/czMaxPrice';
import { parsePackageSize, Product as GeneralProduct } from '~/Product';
import { ProductValues, ReferencePriceValue } from '../components/czMaxPrice/Table';
import { ReferenceValues } from '../components/czMaxPrice/ProductParametersPanel';
import { Values } from '../utils/buildProductUpdates';

function createValues(czMaxPrice: CzMaxPrice | null, currentValues: Values | null): Values {
	return {
		reference: currentValues?.reference ?? {
			packageSize: {
				value: String(czMaxPrice?.reference.packageSize ?? ''),
				isValid: true,
			},
			basketVersion: czMaxPrice?.reference.referenceBasketVersion ?? null,
		},
		product:
			czMaxPrice?.products.reduce((products: { [id: string]: ProductValues }, product) => {
				const currentProductValues = currentValues?.product[product.product.id];
				const hasSelectedValidReferencePrice =
					currentProductValues !== undefined &&
					product.referencePrices.find(
						(referencePrice) => referencePrice.calculatedPrice.type === currentProductValues.referencePrice.value?.value
					) !== undefined;
				const referencePrice = product.referencePrices.find((price) => price.calculatedPrice.selected);

				products[product.product.id] = {
					referencePrice: hasSelectedValidReferencePrice
						? currentProductValues.referencePrice
						: {
								value:
									referencePrice !== undefined
										? {
												value: referencePrice.calculatedPrice.type,
												parentValue: null,
												referencePriceItem: null,
										  }
										: null,
								isValid: product.referencePrices.length === 0 || referencePrice !== undefined,
						  },
					packageSize: currentProductValues?.packageSize ?? {
						value: product.packageSizeForCalculation !== null ? String(product.packageSizeForCalculation) : '',
						isValid: true,
					},
					packageSizeForSingleUnit: currentProductValues?.packageSizeForSingleUnit ?? {
						value: product.packageSizeForSingleUnit !== null ? String(product.packageSizeForSingleUnit) : '',
						isValid: true,
					},
					selected: currentProductValues?.selected ?? product.selected,
				};
				return products;
			}, {}) ?? {},
	};
}

export function useFormValues(czMaxPrice: CzMaxPrice | null) {
	const [localValues, setLocalValues] = useState<Values | null>(null);

	const values = useMemo(() => createValues(czMaxPrice, localValues), [czMaxPrice, localValues]);

	function setReferenceValues(reference: ReferenceValues) {
		setLocalValues({ ...values, reference });
	}

	function setProductValues(
		item: GeneralProduct,
		referencePrice: ReferencePriceValue | null,
		referencePrices: ReferencePrice[],
		packageSize: string,
		packageSizeForSingleUnit: string
	) {
		const currentValue = values.product[item.id];
		setLocalValues({
			reference: values.reference,
			product: {
				...values.product,
				[item.id]: {
					referencePrice: {
						value: referencePrice,
						isValid: referencePrices.length === 0 || referencePrice !== null,
					},
					packageSize: { value: packageSize, isValid: parsePackageSize(packageSize).isValid },
					packageSizeForSingleUnit: {
						value: packageSizeForSingleUnit,
						isValid: parsePackageSize(packageSizeForSingleUnit).isValid,
					},
					selected: currentValue.selected,
				},
			},
		});
	}

	function setProductSelected(productValues: { [id: string]: ProductValues }) {
		setLocalValues({ reference: values.reference, product: productValues });
	}

	function resetValues() {
		setLocalValues(null);
	}

	return { values, setReferenceValues, setProductValues, setProductSelected, resetValues };
}
